import type { Currency } from '@/lib/currency';
import { normalizeDealType, type DealType } from '@/lib/dealTypes';
import {
  createEmptyFinancingFields,
  type FinancingFormFields,
} from '@/lib/financingTerms';
import { DEAL_CONSTRAINTS, type DealConstraintId } from '../../lib/dealConstraints';
import { normalizePropertyType } from '@/lib/listing-form/propertyTypes';

const MAX_NAME = 80;
const MAX_TEXT = 5000;
const CONSTRAINT_IDS = new Set(DEAL_CONSTRAINTS.map((c) => c.id));

/** Allowlisted template payload keys. Never includes address, coords, images, or asking price. */
export type ListingTemplateData = {
  deal_type: DealType | null;
  property_type: string;
  country: string;
  currency: Currency;
  include_market: boolean;
  state: string;
  age_restricted: boolean;
  deal_constraints: DealConstraintId[];
  deal_constraints_notes: string;
  description: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  financing: Partial<FinancingFormFields>;
};

export type ListingTemplateRow = {
  id: string;
  owner_id: string;
  name: string;
  description: string | null;
  template_data: ListingTemplateData;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
};

const FINANCING_ALLOWLIST = new Set(Object.keys(createEmptyFinancingFields()));

function asString(v: unknown, max = MAX_TEXT): string {
  if (typeof v !== 'string') return '';
  return v.trim().slice(0, max);
}

function asCurrency(v: unknown): Currency {
  return v === 'CAD' || v === 'GBP' || v === 'AUD' || v === 'USD' ? v : 'USD';
}

export function sanitizeTemplateData(raw: unknown): ListingTemplateData | { error: string } {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { error: 'Invalid template data' };
  }
  const src = raw as Record<string, unknown>;

  // Reject property-specific keys if somehow present
  for (const banned of [
    'address',
    'city',
    'zip',
    'latitude',
    'longitude',
    'lat',
    'lng',
    'parcel_id',
    'images',
    'price',
    'arv',
    'repairs',
    'cover_image_url',
  ]) {
    if (banned in src && banned !== 'city') {
      // city only allowed when include_market; still strip street-level banned keys
      if (banned !== 'city') {
        // continue stripping below by omit
      }
    }
  }

  const include_market = Boolean(src.include_market);
  const deal_type = src.deal_type == null || src.deal_type === ''
    ? null
    : normalizeDealType(src.deal_type);
  if (src.deal_type && !deal_type) {
    return { error: 'Invalid deal type in template' };
  }

  const constraintsRaw = Array.isArray(src.deal_constraints) ? src.deal_constraints : [];
  const deal_constraints = constraintsRaw.filter(
    (id): id is DealConstraintId => typeof id === 'string' && CONSTRAINT_IDS.has(id as DealConstraintId)
  );

  const financingSrc =
    src.financing && typeof src.financing === 'object' && !Array.isArray(src.financing)
      ? (src.financing as Record<string, unknown>)
      : {};
  const financing: Partial<FinancingFormFields> = {};
  for (const [key, val] of Object.entries(financingSrc)) {
    if (!FINANCING_ALLOWLIST.has(key)) continue;
    // Never persist property-specific loan balances / arrears as reusable defaults unless explicitly text notes
    if (
      [
        'existing_loan_balance',
        'arrears_amount',
        'underlying_balance',
        'balloon_due_date',
        'loan_maturity_date',
        'first_payment_date',
      ].includes(key)
    ) {
      continue;
    }
    if (typeof val === 'boolean') {
      (financing as Record<string, boolean>)[key] = val;
    } else if (val != null) {
      (financing as Record<string, string>)[key] = String(val).slice(0, 2000);
    }
  }

  return {
    deal_type,
    property_type: normalizePropertyType(asString(src.property_type, 80)),
    country: asString(src.country, 8) || 'US',
    currency: asCurrency(src.currency),
    include_market,
    state: include_market ? asString(src.state, 40) : '',
    age_restricted: Boolean(src.age_restricted),
    deal_constraints,
    deal_constraints_notes: asString(src.deal_constraints_notes, 2000),
    description: asString(src.description, MAX_TEXT),
    contact_name: asString(src.contact_name, 120),
    contact_phone: asString(src.contact_phone, 40),
    contact_email: asString(src.contact_email, 120),
    financing,
  };
}

export function sanitizeTemplateName(name: unknown): string | { error: string } {
  if (typeof name !== 'string') return { error: 'Template name is required' };
  const trimmed = name.trim().slice(0, MAX_NAME);
  if (!trimmed) return { error: 'Template name is required' };
  return trimmed;
}

export type TemplateApplyTarget = {
  deal_type: DealType | '';
  property_type: string;
  country: string;
  currency: Currency;
  currencyOverridden: boolean;
  state: string;
  age_restricted: boolean;
  deal_constraints: DealConstraintId[];
  deal_constraints_notes: string;
  description: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  financing: FinancingFormFields;
};

/**
 * Apply template onto form values while preserving property-specific fields
 * (address, city unless market template, zip, coords, images, price, arv, repairs).
 */
export function applyTemplateToFormValues(
  current: TemplateApplyTarget & { city: string; address: string },
  data: ListingTemplateData
): TemplateApplyTarget & { city: string; address: string; dealTypeChanged: boolean } {
  const dealTypeChanged =
    Boolean(data.deal_type) && data.deal_type !== (current.deal_type || null);
  const financing = { ...createEmptyFinancingFields(), ...data.financing };

  return {
    ...current,
    deal_type: data.deal_type ?? '',
    property_type: data.property_type || current.property_type,
    country: data.country || current.country,
    currency: data.currency || current.currency,
    currencyOverridden: true,
    state: data.include_market && data.state ? data.state : current.state,
    city: current.city,
    address: current.address,
    age_restricted: data.age_restricted,
    deal_constraints: data.deal_constraints,
    deal_constraints_notes: data.deal_constraints_notes,
    description: data.description || current.description,
    contact_name: data.contact_name || current.contact_name,
    contact_phone: data.contact_phone || current.contact_phone,
    contact_email: data.contact_email || current.contact_email,
    financing: dealTypeChanged || data.deal_type
      ? financing
      : { ...current.financing, ...data.financing },
    dealTypeChanged,
  };
}

export function buildTemplateDataFromForm(values: {
  deal_type: DealType | '';
  property_type: string;
  country: string;
  currency: Currency;
  state: string;
  age_restricted: boolean;
  deal_constraints: DealConstraintId[];
  deal_constraints_notes: string;
  description: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  financing: FinancingFormFields;
  include_market: boolean;
}): ListingTemplateData | { error: string } {
  return sanitizeTemplateData({
    deal_type: values.deal_type || null,
    property_type: values.property_type,
    country: values.country,
    currency: values.currency,
    include_market: values.include_market,
    state: values.include_market ? values.state : '',
    age_restricted: values.age_restricted,
    deal_constraints: values.deal_constraints,
    deal_constraints_notes: values.deal_constraints_notes,
    description: values.description,
    contact_name: values.contact_name,
    contact_phone: values.contact_phone,
    contact_email: values.contact_email,
    financing: values.financing,
  });
}
